import React, { useState, useEffect, useRef } from 'react';
import { taskAPI, facilityAPI } from '../services/api';
import { useLocation } from 'react-router-dom';
import { ListChecks, Plus, Edit2, Trash2, CheckCircle, Clock, AlertTriangle, User, UserPlus, Mail, Bell, X, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';

const PRIORITY_BAR = { critical: '#DC2626', high: '#F59E0B', medium: '#0066FF', low: '#94a3b8' };

const BADGE = {
  overdue: { bg: '#FEF2F2', color: '#DC2626' },
  critical: { bg: '#FEF2F2', color: '#DC2626' },
  high: { bg: '#FFFBEB', color: '#D97706' },
  medium: { bg: '#EFF6FF', color: '#0066FF' },
  low: { bg: '#F8FAFC', color: '#6B7280' },
  completed: { bg: '#F0FDF4', color: '#16A34A' },
  pending: { bg: '#F8FAFC', color: '#6B7280' },
  in_progress: { bg: '#EFF6FF', color: '#0066FF' },
};

function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filters, setFilters] = useState({ facilityId: '', status: '', priority: '', taskType: '' });
  const [showCompleted, setShowCompleted] = useState(false);
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);
  const highlightRef = useRef(null);
  const location = useLocation();
  const [formData, setFormData] = useState({
    facilityId: '', title: '', description: '', dueDate: '',
    priority: 'medium', regulation: '', status: 'pending',
    assignedToName: '', assignedToEmail: '',
    recipients: [], newRecipientName: '', newRecipientEmail: '', newRecipientRole: 'assignee',
    taskType: 'general', customReminderDays: '',
  });

  useEffect(() => { loadData(); }, [filters]);

  const loadData = async () => {
    try {
      const [tasksRes, facilitiesRes] = await Promise.all([
        taskAPI.getAll(filters), facilityAPI.getAll()
      ]);
      const loadedTasks = tasksRes.data.tasks || [];
      setTasks(loadedTasks);

      // Auto-open task from URL param
      const params = new URLSearchParams(window.location.search);
      const taskId = params.get('taskId');
      if (taskId) {
        const task = loadedTasks.find(t => String(t.id) === taskId);
        if (task) {
          setHighlightedTaskId(parseInt(taskId));
          setEditingTask(task);
          setFormData(prev => ({
            ...prev,
            facilityId: task.facility_id || '',
            title: task.title || '',
            description: task.description || '',
            taskType: task.task_type || 'general',
            priority: task.priority || 'medium',
            status: task.status || 'pending',
            dueDate: task.due_date ? task.due_date.split('T')[0] : '',
            regulation: task.regulatory_requirement || '',
          }));
          setShowModal(true);
        }
      }
      setFacilities(facilitiesRes.data.facilities || []);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        customReminderDays: formData.customReminderDays
          ? formData.customReminderDays.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d))
          : null
      };
      if (editingTask) {
        await taskAPI.update(editingTask.id, payload);
        toast.success('Task updated!');
      } else {
        await taskAPI.create(payload);
        toast.success('Task created!');
      }
      setShowModal(false);
      setEditingTask(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save task');
    }
  };

  const handleComplete = async (taskId) => {
    try {
      await taskAPI.update(taskId, { status: 'completed' });
      toast.success('Task completed!');
      loadData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await taskAPI.delete(id);
      toast.success('Task deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      facilityId: task.facility_id || '',
      title: task.title || '',
      description: task.description || '',
      dueDate: task.due_date ? task.due_date.split('T')[0] : '',
      priority: task.priority || 'medium',
      regulation: task.regulation || '',
      status: task.status || 'pending',
      assignedToName: task.assigned_to || '',
      assignedToEmail: task.assigned_to_email || '',
      recipients: task.recipients || [],
      newRecipientName: '', newRecipientEmail: '', newRecipientRole: 'assignee',
      taskType: task.task_type || 'general',
      customReminderDays: task.custom_reminder_days ? task.custom_reminder_days.join(', ') : '',
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      facilityId: '', title: '', description: '', dueDate: '',
      priority: 'medium', regulation: '', status: 'pending',
      assignedToName: '', assignedToEmail: '',
      recipients: [], newRecipientName: '', newRecipientEmail: '', newRecipientRole: 'assignee',
    recipients: [], newRecipientName: '', newRecipientEmail: '', newRecipientRole: 'assignee',
      taskType: 'general', customReminderDays: '',
    });
  };

  const getDaysUntil = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const due = new Date(dueDate); due.setHours(0,0,0,0);
    return Math.round((due - today) / 86400000);
  };

  const getDueBadge = (task) => {
    if (task.status === 'completed') return null;
    if (task.status === 'overdue') return { text: 'Overdue', ...BADGE.overdue };
    const days = getDaysUntil(task.due_date);
    if (days === null) return null;
    if (days < 0) return { text: `${Math.abs(days)}d late`, ...BADGE.overdue };
    if (days === 0) return { text: 'Due today', ...BADGE.critical };
    if (days <= 7) return { text: `${days}d`, ...BADGE.high };
    if (days <= 30) return { text: `${days}d`, ...BADGE.medium };
    const months = Math.round(days / 30);
    return { text: `${months}mo`, ...BADGE.low };
  };

  const getDefaultReminders = (type) => {
    switch (type) {
      case 'permit_renewal': return '365, 270, 180, 90, 30, 7';
      case 'stack_test': return '90, 30, 14, 7';
      default: return '30, 14, 7, 1';
    }
  };

  const getInitials = (name) => {
    if (!name) return '';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Group tasks
  const activeTasks = tasks.filter(t => t.status !== 'completed');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  const overdue = activeTasks.filter(t => {
    if (t.status === 'overdue') return true;
    const days = getDaysUntil(t.due_date);
    return days !== null && days < 0;
  });

  const dueThisWeek = activeTasks.filter(t => {
    if (overdue.find(o => o.id === t.id)) return false;
    const days = getDaysUntil(t.due_date);
    return days !== null && days >= 0 && days <= 7;
  });

  const upcoming = activeTasks.filter(t =>
    !overdue.find(o => o.id === t.id) && !dueThisWeek.find(d => d.id === t.id)
  );

  const inputStyle = {
    width: '100%', padding: '9px 12px', border: '1px solid var(--color-card-border)',
    borderRadius: 8, fontSize: 14, fontFamily: 'var(--font-body)', boxSizing: 'border-box',
    background: 'var(--color-card-bg)', color: 'var(--color-text-primary)'
  };

  const TaskCard = ({ task }) => {
    const badge = getDueBadge(task);
    const barColor = task.status === 'overdue' || getDaysUntil(task.due_date) < 0
      ? PRIORITY_BAR.critical
      : PRIORITY_BAR[task.priority] || PRIORITY_BAR.medium;

    return (
      <div className="task-card">
        <div className="task-bar" style={{ background: barColor }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)' }}>
              {task.title}
            </span>
            {task.task_type === 'permit_renewal' && (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#D97706', background: '#FFFBEB', padding: '1px 6px', borderRadius: 4 }}>⚠ Permit Shield</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 14, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
            {task.facility_name && <span>📍 {task.facility_name}</span>}
            {task.due_date && <span>📅 {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
            {task.assigned_to && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#EFF6FF', color: '#0066FF', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-heading)' }}>
                  {getInitials(task.assigned_to)}
                </div>
                {task.assigned_to}
              </span>
            )}
            {task.regulation && <span>§ {task.regulation}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {badge && (
            <span className="badge" style={{ background: badge.bg, color: badge.color }}>{badge.text}</span>
          )}
          {task.status === 'completed' && (
            <span className="badge" style={{ background: '#F0FDF4', color: '#16A34A' }}>Done</span>
          )}
          <button onClick={() => handleEdit(task)} className="icon-btn" title="Edit"><Edit2 size={13} /></button>
          {task.status !== 'completed' && (
            <button onClick={() => handleComplete(task.id)} className="icon-btn" title="Mark complete"
              style={{ color: '#16A34A', borderColor: '#BBF7D0' }}>
              <CheckCircle size={13} />
            </button>
          )}
          <button onClick={() => handleDelete(task.id)} className="icon-btn"
            style={{ color: '#DC2626', borderColor: '#FECACA' }} title="Delete">
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    );
  };

  const GroupHeader = ({ label, count, color, icon: Icon }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <Icon size={14} color={color} />
      <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{label}</span>
      <span style={{ background: 'var(--color-card-border)', borderRadius: 20, padding: '1px 8px', fontSize: 12, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>{count}</span>
    </div>
  );

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: 'var(--color-text-muted)' }}>Loading...</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div className="page-title">Tasks</div>
          <div className="page-subtitle">
            {activeTasks.length} active · {overdue.length > 0 ? `${overdue.length} overdue · ` : ''}{completedTasks.length} completed
          </div>
        </div>
        <button onClick={() => { setEditingTask(null); resetForm(); setShowModal(true); }} className="btn btn-primary">
          <Plus size={15} /> Add Task
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
        <select value={filters.facilityId} onChange={e => setFilters({...filters, facilityId: e.target.value})} style={{ ...inputStyle, width: 'auto', minWidth: 160 }}>
          <option value="">All facilities</option>
          {facilities.map(f => <option key={f.id} value={f.id}>{f.facility_name || f.name}</option>)}
        </select>
        <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})} style={{ ...inputStyle, width: 'auto', minWidth: 130 }}>
          <option value="">All statuses</option>
          <option value="overdue">Overdue</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
        </select>
        <select value={filters.priority} onChange={e => setFilters({...filters, priority: e.target.value})} style={{ ...inputStyle, width: 'auto', minWidth: 130 }}>
          <option value="">All priorities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select value={filters.taskType} onChange={e => setFilters({...filters, taskType: e.target.value})} style={{ ...inputStyle, width: 'auto', minWidth: 140 }}>
          <option value="">All types</option>
          <option value="general">General</option>
          <option value="reporting">Reporting</option>
          <option value="stack_test">Stack test</option>
          <option value="permit_renewal">Permit renewal</option>
          <option value="monitoring">Monitoring</option>
        </select>
      </div>

      {/* Empty state */}
      {tasks.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}>
          <ListChecks size={48} color="#CBD5E1" style={{ margin: '0 auto 16px', display: 'block' }} />
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 8 }}>No tasks yet</div>
          <div style={{ fontSize: 14, color: 'var(--color-text-muted)', marginBottom: 24 }}>Upload a permit to auto-generate tasks, or create one manually</div>
          <button onClick={() => setShowModal(true)} className="btn btn-primary"><Plus size={15} /> Create Task</button>
        </div>
      )}

      {/* Overdue group */}
      {overdue.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <GroupHeader label="Overdue" count={overdue.length} color="#DC2626" icon={AlertTriangle} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {overdue.map(t => <TaskCard key={t.id} task={t} />)}
          </div>
        </div>
      )}

      {/* Due this week */}
      {dueThisWeek.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <GroupHeader label="Due this week" count={dueThisWeek.length} color="#D97706" icon={Clock} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {dueThisWeek.map(t => <TaskCard key={t.id} task={t} />)}
          </div>
        </div>
      )}

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <GroupHeader label="Upcoming" count={upcoming.length} color="#6B7280" icon={Clock} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {upcoming.map(t => <TaskCard key={t.id} task={t} />)}
          </div>
        </div>
      )}

      {/* Completed - collapsible */}
      {completedTasks.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div
            onClick={() => setShowCompleted(!showCompleted)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: showCompleted ? 10 : 0, cursor: 'pointer' }}
          >
            {showCompleted ? <ChevronDown size={14} color="#6B7280" /> : <ChevronRight size={14} color="#6B7280" />}
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Completed</span>
            <span style={{ background: 'var(--color-card-border)', borderRadius: 20, padding: '1px 8px', fontSize: 12, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>{completedTasks.length}</span>
          </div>
          {showCompleted && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, opacity: 0.7 }}>
              {completedTasks.map(t => <TaskCard key={t.id} task={t} />)}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 620, maxHeight: '92vh', overflowY: 'auto' }}>
            <div style={{ padding: '18px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
              <div style={{ fontFamily: 'var(--font-heading)', fontSize: 17, fontWeight: 700, color: '#0A1628' }}>{editingTask ? 'Edit Task' : 'Create Compliance Task'}</div>
              <button onClick={() => { setShowModal(false); setEditingTask(null); }} className="icon-btn"><X size={16} /></button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '14px 18px' }}>

              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Task Title *</label>
                <input type="text" required value={formData.title}
                  onChange={e => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g. Annual Stack Test — Boiler #1" style={inputStyle} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label className="form-label">Task Type</label>
                  <select value={formData.taskType} onChange={e => setFormData({...formData, taskType: e.target.value, customReminderDays: getDefaultReminders(e.target.value)})} style={inputStyle}>
                    <option value="general">General compliance</option>
                    <option value="reporting">Reporting (SAMR etc.)</option>
                    <option value="stack_test">Stack testing</option>
                    <option value="permit_renewal">Permit renewal</option>
                    <option value="monitoring">Monitoring</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Facility</label>
                  <select value={formData.facilityId} onChange={e => setFormData({...formData, facilityId: e.target.value})} style={inputStyle}>
                    <option value="">No facility</option>
                    {facilities.map(f => <option key={f.id} value={f.id}>{f.facility_name || f.name}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label className="form-label">Due Date</label>
                  <input type="date" value={formData.dueDate} onChange={e => setFormData({...formData, dueDate: e.target.value})} style={inputStyle} />
                </div>
                <div>
                  <label className="form-label">Priority</label>
                  <select value={formData.priority} onChange={e => setFormData({...formData, priority: e.target.value})} style={inputStyle}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Regulatory Requirement</label>
                <input type="text" value={formData.regulation}
                  onChange={e => setFormData({...formData, regulation: e.target.value})}
                  placeholder="e.g. 40 CFR 60.48c, Permit Condition 5.2.1" style={inputStyle} />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Description</label>
                <textarea value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  rows={2} placeholder="Additional details..." style={{ ...inputStyle, resize: 'vertical' }} />
              </div>

              {/* Assignment & Watchers */}
              <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '11px 14px', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                  <UserPlus size={14} color="#0066FF" />
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#1E40AF', fontFamily: 'var(--font-body)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Assignment & Watchers</span>
                </div>

                {/* Existing recipients */}
                {formData.recipients && formData.recipients.length > 0 && (
                  <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {formData.recipients.map((r, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'white', borderRadius: 7, padding: '6px 10px', border: '1px solid #BFDBFE' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: r.role === 'assignee' ? '#0066FF' : '#E2E8F0', color: r.role === 'assignee' ? 'white' : '#374151' }}>
                          {r.role === 'assignee' ? 'Assignee' : 'Watcher'}
                        </span>
                        <span style={{ fontSize: 13, color: '#374151', flex: 1, fontFamily: 'var(--font-body)' }}>{r.name || r.email}</span>
                        <span style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'var(--font-body)' }}>{r.email}</span>
                        <button type="button" onClick={() => setFormData({...formData, recipients: formData.recipients.filter((_, j) => j !== i)})}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: 2, display: 'flex', alignItems: 'center' }}>
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new recipient */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 8, alignItems: 'end' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#374151', marginBottom: 3, fontFamily: 'var(--font-body)', fontWeight: 600 }}>Name</label>
                    <input type="text" value={formData.newRecipientName}
                      onChange={e => setFormData({...formData, newRecipientName: e.target.value})}
                      placeholder="Jane Smith" style={{ ...inputStyle, background: 'white', fontSize: 13 }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#374151', marginBottom: 3, fontFamily: 'var(--font-body)', fontWeight: 600 }}>Email</label>
                    <input type="email" value={formData.newRecipientEmail}
                      onChange={e => setFormData({...formData, newRecipientEmail: e.target.value})}
                      placeholder="jane@company.com" style={{ ...inputStyle, background: 'white', fontSize: 13 }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#374151', marginBottom: 3, fontFamily: 'var(--font-body)', fontWeight: 600 }}>Role</label>
                    <select value={formData.newRecipientRole}
                      onChange={e => setFormData({...formData, newRecipientRole: e.target.value})}
                      style={{ ...inputStyle, background: 'white', fontSize: 13 }}>
                      <option value="assignee">Assignee</option>
                      <option value="watcher">Watcher</option>
                    </select>
                  </div>
                  <button type="button"
                    onClick={() => {
                      if (!formData.newRecipientEmail || !formData.newRecipientEmail.includes('@')) return;
                      const newR = { name: formData.newRecipientName, email: formData.newRecipientEmail, role: formData.newRecipientRole };
                      setFormData({...formData, recipients: [...(formData.recipients || []), newR], newRecipientName: '', newRecipientEmail: '', newRecipientRole: 'assignee'});
                    }}
                    style={{ padding: '8px 12px', background: '#0066FF', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4, marginTop: 18 }}>
                    <Plus size={13} /> Add
                  </button>
                </div>
                <div style={{ fontSize: 11, color: '#3B82F6', marginTop: 6, fontFamily: 'var(--font-body)' }}>
                  Assignees receive action-required emails. Watchers receive FYI updates only.
                </div>
              </div>
              {/* Reminders */}
              <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '11px 14px', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <Bell size={14} color="#D97706" />
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#92400E', fontFamily: 'var(--font-body)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email Reminders</span>
                  {formData.taskType === 'permit_renewal' && (
                    <span style={{ fontSize: 10, color: '#D97706', fontFamily: 'var(--font-body)', fontWeight: 600 }}>⚠ Permit Shield schedule pre-set</span>
                  )}
                </div>
                <input type="text" value={formData.customReminderDays}
                  onChange={e => setFormData({...formData, customReminderDays: e.target.value})}
                  placeholder="e.g. 30, 14, 7, 1" style={{ ...inputStyle, background: 'white' }} />
                <div style={{ fontSize: 12, color: '#92400E', marginTop: 5, fontFamily: 'var(--font-body)' }}>Days before due date. Sent to account owner + assignee.</div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" onClick={() => { setShowModal(false); setEditingTask(null); }} className="btn btn-ghost">Cancel</button>
                <button type="submit" className="btn btn-primary">{editingTask ? 'Update' : 'Create'} Task</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <style>{`
        @media (max-width: 768px) {
          .mobile-stack { flex-direction: column !important; }
          .mobile-full { width: 100% !important; min-width: 0 !important; }
          .mobile-hide { display: none !important; }
          .mobile-grid-1 { grid-template-columns: 1fr !important; }
          .mobile-grid-2 { grid-template-columns: repeat(2, 1fr) !important; }
          .mobile-p { padding: 12px 14px !important; }
          .mobile-font { font-size: 13px !important; }
        }
      `}</style>
    </div>
  );
}

export default Tasks;
